import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';
import { tracked } from 'tracked-built-ins';

export type LegalTermsEvent = 'emnoShowLegalTerms' | 'closeModal';

export interface LegalTermsMessage {
  event: LegalTermsEvent;
}

export default class CommunicationsAlertsController extends Controller {
  @service declare messageBus: MessageBusService;
  @service declare state: StateService;

  // EMNO-720: modal state driven by messages the emno-fe threat-dashboard
  // iframe posts. Mirrors the EMNO-485 add-rule pattern in
  // location-overview/communications/settings/controller.ts, minus the
  // context payload (legal-terms content is static) and the showToaster
  // branch (view-only modal, no save/side effect).
  @tracked showLegalTermsModal = false;

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  willDestroy(): void {
    super.willDestroy();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.off('embedded-app-message', this, this.handleMessage);
  }

  get iframeUrl(): string {
    const companyId = this.state.currentCompany.id;
    const locationId = this.state.currentLocation.id;

    return urlBuilder.emno.embeddedThreatDashboardUrl(companyId, locationId);
  }

  get legalTermsModalUrl(): string {
    return urlBuilder.emno.embeddedLegalTermsModalUrl(this.state.currentCompany.id);
  }

  /**
   * The iframe bridge. See app/components/embedded-app.gts — every framed
   * iframe rebroadcasts its messages on `embedded-app-message`, and this
   * controller listens for the two scoped to the legal-terms flow:
   *   - `emnoShowLegalTerms` — from the OUTER threat-dashboard iframe: open
   *     the modal.
   *   - `closeModal` — from the MODAL iframe (Done button): shut the modal.
   * All other events on the bus are ignored (belong to other consumers).
   */
  @action
  handleMessage(message: LegalTermsMessage): void {
    if (message.event === 'emnoShowLegalTerms') {
      this.showLegalTermsModal = true;
    } else if (message.event === 'closeModal') {
      this.showLegalTermsModal = false;
    }
  }

  @action
  closeLegalTermsModal(): void {
    this.showLegalTermsModal = false;
  }
}
